import {
  ActionSheetButton,
  IonActionSheet,
  IonIcon,
  IonLabel,
  IonList,
  IonListHeader,
  IonSpinner,
  IonToast,
  isPlatform,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { SocialSharing } from '@ionic-native/social-sharing';
import { shareOutline, shareSocialOutline } from 'ionicons/icons';
import { orderBy } from 'lodash';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  File,
  Machine,
  PlanEntitlementKey,
  useListMachineFilesQuery,
} from '../../api/graphql/generated';
import {
  useGetMachineFileLink,
  useRestoreFiles,
} from '../../api/hooks/file.hooks';
import { copyToClipboard } from '../../util/clipboard';
import { logger } from '../../util/logger';
import { PaywallViewMore } from '../paywall/paywall-view-more';
import { MachineFileItem } from './machine-file-item';

interface FileVersionsProps {
  machine?: Machine;
  filePath: string | null;
}

const FileVersion = (props: FileVersionsProps): ReactElement => {
  const { machine, filePath } = props;
  const { i18n } = useTranslation();

  const [itemTouched, setItemTouched] = useState<File | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);

  const { data, loading, startPolling, stopPolling } = useListMachineFilesQuery(
    {
      variables: { input: { id: machine?.id ?? '', path: filePath } },
      skip: machine?.id === undefined,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  useIonViewWillEnter(() => {
    setReady(true);
    startPolling(5000);
  });

  useIonViewWillLeave(() => {
    stopPolling();
  });

  const getFileLink = useGetMachineFileLink();
  const restoreFiles = useRestoreFiles();

  const files: File[] = data?.listMachineFiles.items ?? [];

  const orderedFiles = orderBy(files, ['type', 'name'], ['asc', 'asc']);

  let latest: File | undefined = orderedFiles[0];
  const backupVersions: File[] = [];

  for (const index in orderedFiles) {
    if (orderedFiles[index].latest === true) {
      latest = orderedFiles[index];
    } else {
      backupVersions.push(orderedFiles[index]);
    }
  }

  const getShareableLink = async (item: File) => {
    if (itemTouched !== null) {
      setItemTouched(item);
      logger.info('Requesting file link', { item });
      try {
        const url = await getFileLink(item, machine?.id);
        setItemTouched(null);
        if (url) {
          if (isPlatform('capacitor')) {
            SocialSharing.shareWithOptions({
              message: item.name, // not supported on some apps (Facebook, Instagram)
              // subject: 'the subject', // fi. for email
              url,
            });
          } else {
            copyToClipboard(url);
            setCopied(true);
          }
        }
      } catch (error) {
        logger.error('Could not get link to file', { error, item });
      }
    }
  };

  const getOptionsForItem = (item: File | null): ActionSheetButton[] => {
    const buttons: ActionSheetButton[] = [
      {
        text: i18n.t('common:files_get_link'),
        handler: async () => {
          if (item) {
            await getShareableLink(item);
          }
        },
      },
      {
        text: i18n.t('common:cancel'),
        role: 'cancel',
      },
    ];

    if (item?.latest !== true) {
      buttons.unshift({
        text: i18n.t('common:files_restore'),
        role: 'destructive',
        handler: async () => {
          if (machine?.id && item) {
            await restoreFiles(machine?.id, [item]);
          }
        },
      });
    }

    return buttons;
  };

  const shareIcon = isPlatform('ios') ? (
    <IonIcon icon={shareOutline} color="primary" />
  ) : (
    <IonIcon icon={shareSocialOutline} color="primary" />
  );

  const loadingIcon = <IonSpinner name="dots" />;
  if (!isReady || loading) {
    return (
      <div className="full-height-width content-center">
        <IonSpinner />
      </div>
    );
  } else {
    return (
      <>
        <IonList lines="full">
          <IonListHeader>
            <IonLabel>{i18n.t('common:files_latest_version')}</IonLabel>
          </IonListHeader>
          <MachineFileItem
            item={latest}
            ionItemProps={{ lines: 'none' }}
            onPress={() => {
              if (latest) {
                setItemTouched(latest);
              }
            }}
            rightElement={
              itemTouched?.id === latest?.id ? loadingIcon : shareIcon
            }
          />
          {backupVersions.length > 0 && (
            <IonListHeader>
              <IonLabel>{i18n.t('common:files_backup_version')}</IonLabel>
            </IonListHeader>
          )}
          {backupVersions.map((item) => {
            return (
              <MachineFileItem
                key={item.id}
                item={item}
                onPress={() => {
                  setItemTouched(item);
                }}
                rightElement={
                  itemTouched?.id === item.id ? loadingIcon : shareIcon
                }
              />
            );
          })}
          <IonActionSheet
            isOpen={itemTouched !== null}
            header={i18n.t('common:files_actions')}
            onDidDismiss={() => setItemTouched(null)}
            buttons={getOptionsForItem(itemTouched)}
          />
          <IonToast
            isOpen={copied}
            onDidDismiss={() => setCopied(false)}
            color={'success'}
            position={'bottom'}
            message={i18n.t('common:file_copied_to_clipboard')}
            duration={1500}
          />
        </IonList>
        <div className="ion-padding ion-text-center">
          <PaywallViewMore
            planEntitlementKey={PlanEntitlementKey.MACHINE_FILE_VERSION_MAX_AGE}
          />
        </div>
      </>
    );
  }
};

export { FileVersion };
